import React, { useState, useEffect } from 'react';
import { useSelector } from 'react-redux';
import moment from 'moment';
import {
  Box,
  Button,
  Divider,
  FormControl,
  FormControlLabel,
  FormHelperText,
  FormLabel,
  Grid,
  InputAdornment,
  InputLabel,
  MenuItem,
  Radio,
  RadioGroup,
  Select,
  TextField,
  Typography,
  Alert,
} from '@mui/material';
import HotelIcon from '@material-ui/icons/Hotel';
import BathtubIcon from '@material-ui/icons/Bathtub';
import PhoneIcon from '@material-ui/icons/Phone';
import EmailIcon from '@material-ui/icons/Email';
import PersonIcon from '@material-ui/icons/Person';
import CalendarTodayIcon from '@material-ui/icons/CalendarToday';

const BuyCertificateForm = ({ onSubmit, openFlow, certificateType = 'inventory', onCancel }) => {
  const userProfile = useSelector((state) => state?.users?.user_profile);

  const [formData, setFormData] = useState({
    number_of_bedrooms: 1,
    number_of_bathrooms: 1,
    number_of_appliances: 1,
    gas_type: 'Natural',
    contact_type: 'tenant',
    name: '',
    email: '',
    contact: '',
    furnishing: 'Fully Furnished',
    conduct_date: moment().add(1, 'day').format('YYYY-MM-DD'),
    key_pick_up: 'Occupied - Knock/Ring Door',
    key_pick_up_address: '',
    key_drop_off: 'Not Required',
    key_drop_off_address: '',
    agent_contact: '',
    agent_email: '',
    notes: '',
    confirm_date: 'false',
    notify_date: 'false',
  });

  const [errors, setErrors] = useState({});

  const getAgentAddress = () => {
    const branch = userProfile?.agent?.branch;
    return [branch?.branch_address, branch?.postcode, branch?.city, branch?.country]
      .filter(Boolean)
      .join(', ');
  };

  const getAgentName = () => {
    return `${userProfile?.first_name || ''} ${userProfile?.last_name || ''}`.trim() || 'Agent';
  };

  const getAgentEmail = () => {
    return userProfile?.email || userProfile?.agent?.branch?.email || '';
  };

  const getAgentContact = () => {
    return userProfile?.contact_number || userProfile?.agent?.branch?.contact_number || '';
  };

  const getLandlordName = () => {
    const landlords = openFlow?.open_flow_landlords || [];
    const lead = landlords.find((l) => l.is_lead) || landlords[0];
    return lead ? `${lead?.first_name || ''} ${lead?.last_name || ''}`.trim() : '';
  };

  const getLandlordEmail = () => {
    const landlords = openFlow?.open_flow_landlords || [];
    const lead = landlords.find((l) => l.is_lead) || landlords[0];
    return lead?.email || '';
  };

  const getLandlordContact = () => {
    const landlords = openFlow?.open_flow_landlords || [];
    const lead = landlords.find((l) => l.is_lead) || landlords[0];
    return lead?.contact_number || lead?.number || '';
  };

  const getTenantName = () => {
    const tenants = openFlow?.tenants || [];
    const lead = tenants.find((t) => t.is_lead) || tenants[0];
    if (lead) {
      return (
        `${lead?.app_user?.user?.first_name || ''} ${lead?.app_user?.user?.last_name || ''}`.trim() ||
        `${lead?.first_name || ''} ${lead?.last_name || ''}`.trim()
      );
    }
    return '';
  };

  const getTenantEmail = () => {
    const tenants = openFlow?.tenants || [];
    const lead = tenants.find((t) => t.is_lead) || tenants[0];
    return lead?.app_user?.user?.email || lead?.email || '';
  };

  const getTenantContact = () => {
    const tenants = openFlow?.tenants || [];
    const lead = tenants.find((t) => t.is_lead) || tenants[0];
    return lead?.app_user?.user?.contact_number || lead?.contact_number || '';
  };

  // Initial populate of agent details and tenant details
  useEffect(() => {
    const agEmail = getAgentEmail();
    const agContact = getAgentContact();
    const tName = getTenantName();
    const tEmail = getTenantEmail();
    const tContact = getTenantContact();

    setFormData((prev) => ({
      ...prev,
      agent_email: agEmail || prev.agent_email,
      agent_contact: agContact || prev.agent_contact,
      name: tName || prev.name,
      email: tEmail || prev.email,
      contact: tContact || prev.contact,
    }));
  }, [userProfile, openFlow]);

  const handleChange = (field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    if (errors[field]) {
      setErrors((prev) => ({ ...prev, [field]: undefined }));
    }
  };

  const handleContactTypeChange = (val) => {
    if (val === 'tenant') {
      setFormData((prev) => ({
        ...prev,
        contact_type: val,
        name: getTenantName(),
        email: getTenantEmail(),
        contact: getTenantContact(),
      }));
    } else if (val === 'agent') {
      setFormData((prev) => ({
        ...prev,
        contact_type: val,
        name: getAgentName(),
        email: getAgentEmail(),
        contact: getAgentContact(),
      }));
    } else if (val === 'landlord') {
      setFormData((prev) => ({
        ...prev,
        contact_type: val,
        name: getLandlordName(),
        email: getLandlordEmail(),
        contact: getLandlordContact(),
      }));
    } else {
      setFormData((prev) => ({
        ...prev,
        contact_type: val,
        name: '',
        email: '',
        contact: '',
      }));
    }
  };

  const handleKeyPickUpChange = (val) => {
    const addr = val === 'Collect From Agent' ? getAgentAddress() : '';
    setFormData((prev) => ({
      ...prev,
      key_pick_up: val,
      key_pick_up_address: addr,
    }));
  };

  const handleKeyDropOffChange = (val) => {
    const addr = val === 'Return To Agent' ? getAgentAddress() : '';
    setFormData((prev) => ({
      ...prev,
      key_drop_off: val,
      key_drop_off_address: addr,
    }));
  };

  const validate = () => {
    const newErrors = {};
    if (!formData.name?.trim()) newErrors.name = 'Name is required';
    if (!formData.email?.trim()) newErrors.email = 'Email is required';
    if (!formData.contact?.trim()) newErrors.contact = 'Contact number is required';
    if (!formData.conduct_date) newErrors.conduct_date = 'Conduct date is required';
    if (!formData.agent_contact?.trim()) newErrors.agent_contact = 'Agent contact is required';
    if (!formData.agent_email?.trim()) newErrors.agent_email = 'Agent email is required';

    if (
      (formData.key_pick_up === 'Other' || formData.key_pick_up === 'Collect From Agent') &&
      !formData.key_pick_up_address?.trim()
    ) {
      newErrors.key_pick_up_address = 'Address is required';
    }

    if (
      (formData.key_drop_off === 'Other' || formData.key_drop_off === 'Return To Agent') &&
      !formData.key_drop_off_address?.trim()
    ) {
      newErrors.key_drop_off_address = 'Address is required';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = (e) => {
    e?.preventDefault();
    if (!validate()) return;

    const payload = {
      ...formData,
      number_of_bedrooms: Number(formData.number_of_bedrooms) || 1,
      number_of_bathrooms: Number(formData.number_of_bathrooms) || 1,
      number_of_appliances: Number(formData.number_of_appliances) || 1,
      notes: `${formData.notes || ''}/nappliances:${formData.number_of_appliances || 1}/ngas_type:${formData.gas_type || 'Natural'}`,
      conduct_date: formData.conduct_date ? moment(formData.conduct_date).format('YYYY-MM-DD') : moment().format('YYYY-MM-DD'),
      price: 'POA',
      confirm_date: formData.confirm_date === 'true' || formData.confirm_date === true,
      notify_date: formData.notify_date === 'true' || formData.notify_date === true,
    };

    if (onSubmit) {
      onSubmit(payload);
    }
  };

  return (
    <Box component="form" noValidate onSubmit={handleSubmit} sx={{ mt: 1 }}>
      {openFlow?.is_renewed && (
        <Alert severity="info" sx={{ mb: 2.5 }}>
          In order to faciliate the purchase of your property visit, please enter the details below
        </Alert>
      )}

      <Typography sx={{ mb: 3, color: 'error.main', fontWeight: 700, fontSize: '15px' }}>
        <strong>Cost:</strong> £POA +VAT
      </Typography>

      <Grid container spacing={2.5}>
        <Grid item xs={12} sm={4}>
          <TextField
            fullWidth
            type="number"
            label="Number Of Bedrooms"
            value={formData.number_of_bedrooms}
            onChange={(e) => handleChange('number_of_bedrooms', Math.max(1, parseInt(e.target.value) || 1))}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <HotelIcon fontSize="small" sx={{ color: '#64748b' }} />
                </InputAdornment>
              ),
            }}
          />
        </Grid>

        <Grid item xs={12} sm={4}>
          <TextField
            fullWidth
            type="number"
            label="Number Of Bathrooms"
            value={formData.number_of_bathrooms}
            onChange={(e) => handleChange('number_of_bathrooms', Math.max(1, parseInt(e.target.value) || 1))}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <BathtubIcon fontSize="small" sx={{ color: '#64748b' }} />
                </InputAdornment>
              ),
            }}
          />
        </Grid>

        <Grid item xs={12}>
          <Typography variant="body2" sx={{ fontWeight: 600, color: '#102a43', mt: 1 }}>
            Access Details
          </Typography>
          <Divider sx={{ my: 1.5 }} />
        </Grid>

        <Grid item xs={12}>
          <FormControl component="fieldset">
            <FormLabel component="legend" sx={{ fontSize: '13px', fontWeight: 500, color: '#486581' }}>
              Please select contact person
            </FormLabel>
            <RadioGroup
              row
              value={formData.contact_type}
              onChange={(e) => handleContactTypeChange(e.target.value)}
            >
              <FormControlLabel value="tenant" control={<Radio size="small" />} label="Tenant" />
              <FormControlLabel value="agent" control={<Radio size="small" />} label="Agent" />
              <FormControlLabel value="landlord" control={<Radio size="small" />} label="Landlord" />
              <FormControlLabel value="other" control={<Radio size="small" />} label="Other" />
            </RadioGroup>
          </FormControl>
        </Grid>

        <Grid item xs={12} sm={4}>
          <TextField
            fullWidth
            label="Name"
            value={formData.name}
            onChange={(e) => handleChange('name', e.target.value)}
            error={Boolean(errors.name)}
            helperText={errors.name}
            placeholder="Enter Name"
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <PersonIcon fontSize="small" sx={{ color: '#64748b' }} />
                </InputAdornment>
              ),
            }}
          />
        </Grid>

        <Grid item xs={12} sm={4}>
          <TextField
            fullWidth
            label="Email"
            type="email"
            value={formData.email}
            onChange={(e) => handleChange('email', e.target.value)}
            error={Boolean(errors.email)}
            helperText={errors.email}
            placeholder="Enter Email"
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <EmailIcon fontSize="small" sx={{ color: '#64748b' }} />
                </InputAdornment>
              ),
            }}
          />
        </Grid>

        <Grid item xs={12} sm={4}>
          <TextField
            fullWidth
            label="Contact Number"
            value={formData.contact}
            onChange={(e) => handleChange('contact', e.target.value)}
            error={Boolean(errors.contact)}
            helperText={errors.contact}
            placeholder="Enter Contact Number"
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <PhoneIcon fontSize="small" sx={{ color: '#64748b' }} />
                </InputAdornment>
              ),
            }}
          />
        </Grid>

        <Grid item xs={12} sm={6}>
          <FormControl component="fieldset">
            <FormLabel component="legend" sx={{ fontSize: '13px', fontWeight: 500, color: '#486581' }}>
              Furnishing
            </FormLabel>
            <RadioGroup
              row
              value={formData.furnishing}
              onChange={(e) => handleChange('furnishing', e.target.value)}
            >
              <FormControlLabel value="Fully Furnished" control={<Radio size="small" />} label="Furnished" />
              <FormControlLabel value="Part Furnished" control={<Radio size="small" />} label="Part Furnished" />
              <FormControlLabel value="UnFurnished" control={<Radio size="small" />} label="UnFurnished" />
            </RadioGroup>
          </FormControl>
        </Grid>

        <Grid item xs={12} sm={6}>
          <TextField
            fullWidth
            type="date"
            label="Conduct Date"
            value={formData.conduct_date}
            onChange={(e) => handleChange('conduct_date', e.target.value)}
            error={Boolean(errors.conduct_date)}
            helperText={errors.conduct_date}
            InputLabelProps={{ shrink: true }}
            inputProps={{ min: moment().format('YYYY-MM-DD') }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <CalendarTodayIcon fontSize="small" sx={{ color: '#64748b' }} />
                </InputAdornment>
              ),
            }}
          />
        </Grid>

        <Grid item xs={12}>
          <Typography variant="body2" sx={{ fontWeight: 600, color: '#102a43', mt: 1 }}>
            Key Pick Up Information (How do we gain access?)
          </Typography>
          <Divider sx={{ my: 1.5 }} />
        </Grid>

        <Grid item xs={12} sm={6}>
          <FormControl fullWidth>
            <InputLabel id="key-pick-up-label">Key Pick Up</InputLabel>
            <Select
              labelId="key-pick-up-label"
              label="Key Pick Up"
              value={formData.key_pick_up}
              onChange={(e) => handleKeyPickUpChange(e.target.value)}
            >
              <MenuItem value="Occupied - Knock/Ring Door">Occupied - Knock/Ring Door</MenuItem>
              <MenuItem value="Collect From Agent">Collect From Agent</MenuItem>
              <MenuItem value="Collect From Neighbour">Collect From Neighbour</MenuItem>
              <MenuItem value="Collect From LockBox">Collect From LockBox</MenuItem>
              <MenuItem value="Other">Other</MenuItem>
            </Select>
          </FormControl>
        </Grid>

        <Grid item xs={12} sm={6}>
          <TextField
            fullWidth
            label="Key pick up address"
            value={formData.key_pick_up_address}
            onChange={(e) => handleChange('key_pick_up_address', e.target.value)}
            disabled={!(formData.key_pick_up === 'Other' || formData.key_pick_up === 'Collect From Agent')}
            error={Boolean(errors.key_pick_up_address)}
            helperText={errors.key_pick_up_address}
            placeholder="Address"
          />
        </Grid>

        <Grid item xs={12} sm={6}>
          <FormControl fullWidth>
            <InputLabel id="key-drop-off-label">Key Drop Off</InputLabel>
            <Select
              labelId="key-drop-off-label"
              label="Key Drop Off"
              value={formData.key_drop_off}
              onChange={(e) => handleKeyDropOffChange(e.target.value)}
            >
              <MenuItem value="Not Required">Not Required</MenuItem>
              <MenuItem value="Return To Agent">Return To Agent</MenuItem>
              <MenuItem value="Post through letterbox">Post through letterbox</MenuItem>
              <MenuItem value="Return to Neighbour">Return to Neighbour</MenuItem>
              <MenuItem value="Return to Lockbox">Return to Lockbox</MenuItem>
              <MenuItem value="Retain until further instruction">Retain until further instruction</MenuItem>
              <MenuItem value="Other">Other</MenuItem>
            </Select>
          </FormControl>
        </Grid>

        <Grid item xs={12} sm={6}>
          <TextField
            fullWidth
            label="Key drop off address"
            value={formData.key_drop_off_address}
            onChange={(e) => handleChange('key_drop_off_address', e.target.value)}
            disabled={!(formData.key_drop_off === 'Other' || formData.key_drop_off === 'Return To Agent')}
            error={Boolean(errors.key_drop_off_address)}
            helperText={errors.key_drop_off_address}
            placeholder="Address"
          />
        </Grid>

        <Grid item xs={12}>
          <Typography variant="body2" sx={{ fontWeight: 600, color: '#102a43', mt: 1 }}>
            Agent Details
          </Typography>
          <Divider sx={{ my: 1.5 }} />
        </Grid>

        <Grid item xs={12} sm={6}>
          <TextField
            fullWidth
            label="Agent Contact"
            value={formData.agent_contact}
            onChange={(e) => handleChange('agent_contact', e.target.value)}
            error={Boolean(errors.agent_contact)}
            helperText={errors.agent_contact}
            placeholder="Enter Contact Number"
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <PhoneIcon fontSize="small" sx={{ color: '#64748b' }} />
                </InputAdornment>
              ),
            }}
          />
        </Grid>

        <Grid item xs={12} sm={6}>
          <TextField
            fullWidth
            type="email"
            label="Agent Email"
            value={formData.agent_email}
            onChange={(e) => handleChange('agent_email', e.target.value)}
            error={Boolean(errors.agent_email)}
            helperText={errors.agent_email}
            placeholder="Enter Agent Email"
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <EmailIcon fontSize="small" sx={{ color: '#64748b' }} />
                </InputAdornment>
              ),
            }}
          />
        </Grid>

        <Grid item xs={12}>
          <TextField
            fullWidth
            multiline
            rows={2}
            label="Notes"
            value={formData.notes}
            onChange={(e) => handleChange('notes', e.target.value)}
            placeholder="Notes"
          />
        </Grid>

        <Grid item xs={12} sm={6}>
          <FormControl component="fieldset">
            <FormLabel component="legend" sx={{ fontSize: '13px', fontWeight: 500, color: '#486581' }}>
              Confirm Date and visit with tenants
            </FormLabel>
            <RadioGroup
              row
              value={formData.confirm_date}
              onChange={(e) => handleChange('confirm_date', e.target.value)}
            >
              <FormControlLabel value="true" control={<Radio size="small" />} label="Yes" />
              <FormControlLabel value="false" control={<Radio size="small" />} label="No" />
            </RadioGroup>
          </FormControl>
        </Grid>

        <Grid item xs={12} sm={6}>
          <FormControl component="fieldset">
            <FormLabel component="legend" sx={{ fontSize: '13px', fontWeight: 500, color: '#486581' }}>
              Notify Date and visit with tenants
            </FormLabel>
            <RadioGroup
              row
              value={formData.notify_date}
              onChange={(e) => handleChange('notify_date', e.target.value)}
            >
              <FormControlLabel value="true" control={<Radio size="small" />} label="Yes" />
              <FormControlLabel value="false" control={<Radio size="small" />} label="No" />
            </RadioGroup>
          </FormControl>
        </Grid>
      </Grid>

      <Divider sx={{ my: 3 }} />

      <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 2 }}>
        <Button
          onClick={onCancel}
          variant="outlined"
          sx={{ color: '#555', borderColor: '#ccc', textTransform: 'none', px: 3 }}
        >
          Cancel
        </Button>
        <Button
          type="submit"
          variant="contained"
          sx={{ backgroundColor: '#0288D1', '&:hover': { backgroundColor: '#0277BD' }, textTransform: 'none', px: 3 }}
        >
          Submit
        </Button>
      </Box>
    </Box>
  );
};

export default BuyCertificateForm;
